import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { BookOpen, Calendar, ChevronLeft, ChevronRight, Save, Plus, Trash2, Eye, Edit2, Target, FileText } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const DAYS = [
  { key: 'lunes', label: 'Lunes' },
  { key: 'martes', label: 'Martes' },
  { key: 'miercoles', label: 'Miércoles' },
  { key: 'jueves', label: 'Jueves' },
  { key: 'viernes', label: 'Viernes' }
];

const emptyDay = { topic: '', activities: '', resources: '', homework: '' };

const getMonday = (d) => {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  date.setDate(diff);
  return date.toISOString().split('T')[0];
};

const addWeeks = (dateStr, weeks) => {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + (weeks * 7));
  return d.toISOString().split('T')[0];
};

const formatWeekRange = (mondayStr) => {
  const mon = new Date(mondayStr + 'T12:00:00');
  const fri = new Date(mon);
  fri.setDate(fri.getDate() + 4);
  const opts = { day: 'numeric', month: 'short' };
  return `${mon.toLocaleDateString('es-ES', opts)} — ${fri.toLocaleDateString('es-ES', opts)}, ${mon.getFullYear()}`;
};

const WeeklyPlanningTab = ({ role }) => {
  const { user } = useAuth();
  const isTeacher = role === 'teacher';
  const isAdmin = role === 'admin';
  const isParent = role === 'parent';
  const canEdit = isTeacher;

  const [currentWeek, setCurrentWeek] = useState(() => getMonday(new Date()));
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [plans, setPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [children, setChildren] = useState([]);

  const [editDays, setEditDays] = useState({});
  const [editObjectives, setEditObjectives] = useState('');
  const [editNotes, setEditNotes] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      let response;
      if (isTeacher) {
        response = await axios.get(`/weekly-plans/teacher?week_start=${currentWeek}`);
        setSubjects(response.data.subjects || []);
        const pls = response.data.plans || [];
        setPlans(pls);
        if (!selectedSubject && response.data.subjects?.length > 0) {
          setSelectedSubject(response.data.subjects[0].id);
        }
      } else if (isParent) {
        response = await axios.get(`/weekly-plans/parent?week_start=${currentWeek}`);
        setSubjects(response.data.subjects || []);
        setChildren(response.data.children || []);
        setPlans(response.data.plans || []);
        if (!selectedSubject && response.data.subjects?.length > 0) {
          setSelectedSubject(response.data.subjects[0].id);
        }
      } else {
        const [plansRes, subjectsRes] = await Promise.all([
          axios.get(`/weekly-plans?week_start=${currentWeek}`),
          axios.get('/subjects')
        ]);
        setSubjects(subjectsRes.data || []);
        setPlans(plansRes.data || []);
        if (!selectedSubject && subjectsRes.data?.length > 0) {
          setSelectedSubject(subjectsRes.data[0].id);
        }
      }
    } catch (error) {
      console.error('Error loading plans:', error);
    } finally {
      setLoading(false);
    }
  }, [currentWeek, isTeacher, isParent, isAdmin]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (selectedSubject && plans.length > 0) {
      const plan = plans.find(p => p.subject_id === selectedSubject && p.week_start === currentWeek);
      setCurrentPlan(plan || null);
      if (plan) {
        setEditDays(plan.days || {});
        setEditObjectives(plan.objectives || '');
        setEditNotes(plan.notes || '');
      } else {
        resetEditForm();
      }
    } else {
      setCurrentPlan(null);
      resetEditForm();
    }
    setEditMode(false);
  }, [selectedSubject, plans, currentWeek]);

  const resetEditForm = () => {
    const days = {};
    DAYS.forEach(d => { days[d.key] = { ...emptyDay }; });
    setEditDays(days);
    setEditObjectives('');
    setEditNotes('');
  };

  const updateDay = (dayKey, field, value) => {
    setEditDays(prev => ({
      ...prev,
      [dayKey]: { ...(prev[dayKey] || emptyDay), [field]: value }
    }));
  };

  const handleSave = async () => {
    if (!selectedSubject) { toast.error('Selecciona una materia'); return; }
    setSaving(true);
    try {
      await axios.post('/weekly-plans', {
        subject_id: selectedSubject,
        week_start: currentWeek,
        days: editDays,
        objectives: editObjectives,
        notes: editNotes
      });
      toast.success(currentPlan ? 'Plan actualizado' : 'Plan creado');
      setEditMode(false);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!currentPlan?.plan_id) return;
    if (!window.confirm('¿Eliminar esta planificación?')) return;
    try {
      await axios.delete(`/weekly-plans/${currentPlan.plan_id}`);
      toast.success('Plan eliminado');
      await loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const selectedSubjectName = subjects.find(s => s.id === selectedSubject)?.name || '';

  const hasPlanForSubject = (subjectId) => plans.some(p => p.subject_id === subjectId && p.week_start === currentWeek);

  return (
    <div className="space-y-6" data-testid="weekly-planning-panel">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-blue-600" /> Planificación Semanal
          </h2>
          <p className="text-slate-500 mt-1">
            {isTeacher ? 'Organiza el contenido semanal de tus materias' : isParent ? 'Consulta la planificación de las materias de tus hijos' : 'Vista general de la planificación semanal'}
          </p>
        </div>
      </div>

      {/* Week Navigation + Subject Selection */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setCurrentWeek(addWeeks(currentWeek, -1))} data-testid="week-prev">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="text-center min-w-[220px]">
                <div className="font-semibold text-slate-800" data-testid="week-label">{formatWeekRange(currentWeek)}</div>
                <div className="text-xs text-slate-500">Semana del {currentWeek}</div>
              </div>
              <Button variant="outline" size="sm" onClick={() => setCurrentWeek(addWeeks(currentWeek, 1))} data-testid="week-next">
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setCurrentWeek(getMonday(new Date()))} className="text-blue-600 ml-2">Hoy</Button>
            </div>
            <div className="flex items-center gap-3">
              <Label className="text-sm font-medium whitespace-nowrap">Materia:</Label>
              <Select value={selectedSubject} onValueChange={setSelectedSubject}>
                <SelectTrigger className="w-[200px]" data-testid="subject-select">
                  <SelectValue placeholder="Seleccionar materia" />
                </SelectTrigger>
                <SelectContent>
                  {subjects.map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      <div className="flex items-center gap-2">
                        {s.name}
                        {hasPlanForSubject(s.id) && <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : !selectedSubject ? (
        <Card><CardContent className="p-12 text-center text-slate-400">
          <BookOpen className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <p className="text-lg">{subjects.length === 0 ? 'No hay materias asignadas' : 'Selecciona una materia'}</p>
        </CardContent></Card>
      ) : editMode ? (
        /* ===== EDIT MODE ===== */
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex justify-between items-center">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Target className="w-5 h-5 text-blue-600" /> Objetivos de la Semana
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <Textarea value={editObjectives} onChange={(e) => setEditObjectives(e.target.value)}
                placeholder="Ej: Al finalizar la semana, los estudiantes podrán..." rows={2} data-testid="objectives-input" />
            </CardContent>
          </Card>

          {DAYS.map(day => (
            <Card key={day.key} className="border-l-4 border-l-blue-500">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold text-blue-700">{day.label}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-xs text-slate-500">Tema</Label>
                  <Input value={editDays[day.key]?.topic || ''} onChange={(e) => updateDay(day.key, 'topic', e.target.value)}
                    placeholder="Tema del día" data-testid={`day-${day.key}-topic`} />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Actividades</Label>
                  <Textarea value={editDays[day.key]?.activities || ''} onChange={(e) => updateDay(day.key, 'activities', e.target.value)}
                    placeholder="Actividades planificadas" rows={2} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">Recursos</Label>
                    <Input value={editDays[day.key]?.resources || ''} onChange={(e) => updateDay(day.key, 'resources', e.target.value)}
                      placeholder="Materiales, libros, etc." />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Tarea</Label>
                    <Input value={editDays[day.key]?.homework || ''} onChange={(e) => updateDay(day.key, 'homework', e.target.value)}
                      placeholder="Asignación para el hogar" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Notas Adicionales</CardTitle></CardHeader>
            <CardContent>
              <Textarea value={editNotes} onChange={(e) => setEditNotes(e.target.value)} placeholder="Observaciones, ajustes, etc." rows={2} />
            </CardContent>
          </Card>

          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => setEditMode(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-plan-btn">
              <Save className="w-4 h-4 mr-2" /> {saving ? 'Guardando...' : 'Guardar Plan'}
            </Button>
          </div>
        </div>
      ) : currentPlan ? (
        /* ===== VIEW MODE ===== */
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Badge className="bg-blue-100 text-blue-700 text-sm py-1 px-3">
              {selectedSubjectName} — {formatWeekRange(currentWeek)}
            </Badge>
            <div className="flex gap-2">
              {canEdit && (
                <>
                  <Button variant="outline" size="sm" onClick={() => setEditMode(true)} data-testid="edit-plan-btn">
                    <Edit2 className="w-4 h-4 mr-1" /> Editar
                  </Button>
                  <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={handleDelete} data-testid="delete-plan-btn">
                    <Trash2 className="w-4 h-4 mr-1" /> Eliminar
                  </Button>
                </>
              )}
              {isAdmin && currentPlan && (
                <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={handleDelete} data-testid="admin-delete-plan-btn">
                  <Trash2 className="w-4 h-4 mr-1" /> Eliminar
                </Button>
              )}
            </div>
          </div>

          {currentPlan.objectives && (
            <Card className="bg-blue-50/50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-start gap-2">
                  <Target className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-blue-800 mb-1">Objetivos de la Semana</p>
                    <p className="text-sm text-blue-700 whitespace-pre-wrap">{currentPlan.objectives}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid gap-3">
            {DAYS.map(day => {
              const dayData = currentPlan.days?.[day.key];
              const hasContent = dayData && (dayData.topic || dayData.activities || dayData.homework);
              return (
                <Card key={day.key} className={`transition-all ${hasContent ? 'border-l-4 border-l-blue-500' : 'opacity-60'}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-800 mb-2">{day.label}</h3>
                        {hasContent ? (
                          <div className="space-y-2">
                            {dayData.topic && (
                              <div className="flex items-start gap-2">
                                <Badge variant="outline" className="text-xs mt-0.5 whitespace-nowrap">Tema</Badge>
                                <p className="text-sm text-slate-700">{dayData.topic}</p>
                              </div>
                            )}
                            {dayData.activities && (
                              <div className="flex items-start gap-2">
                                <Badge variant="outline" className="text-xs mt-0.5 whitespace-nowrap">Actividades</Badge>
                                <p className="text-sm text-slate-600 whitespace-pre-wrap">{dayData.activities}</p>
                              </div>
                            )}
                            <div className="flex gap-4 flex-wrap">
                              {dayData.resources && (
                                <div className="flex items-center gap-1 text-xs text-slate-500">
                                  <FileText className="w-3 h-3" /> {dayData.resources}
                                </div>
                              )}
                              {dayData.homework && (
                                <div className="flex items-center gap-1 text-xs text-amber-600 font-medium">
                                  <BookOpen className="w-3 h-3" /> Tarea: {dayData.homework}
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-slate-400 italic">Sin planificación</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {currentPlan.notes && (
            <Card className="bg-slate-50">
              <CardContent className="p-4">
                <p className="text-sm font-semibold text-slate-700 mb-1">Notas</p>
                <p className="text-sm text-slate-600 whitespace-pre-wrap">{currentPlan.notes}</p>
              </CardContent>
            </Card>
          )}
        </div>
      ) : (
        /* ===== NO PLAN ===== */
        <Card>
          <CardContent className="p-12 text-center">
            <Calendar className="w-16 h-16 mx-auto text-slate-200 mb-4" />
            <p className="text-lg text-slate-500 mb-2">No hay planificación para esta semana</p>
            <p className="text-sm text-slate-400 mb-4">{selectedSubjectName} — {formatWeekRange(currentWeek)}</p>
            {canEdit && (
              <Button onClick={() => { resetEditForm(); setEditMode(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid="create-plan-btn">
                <Plus className="w-4 h-4 mr-2" /> Crear Planificación
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default WeeklyPlanningTab;
